<template>
  <fieldset class="flex flex-wrap gap-050 gap-y-100" role="radiogroup">
    <legend class="sr-only">
      {{ $t('highfrequencygame.quickpickspotselector.legend') }}
    </legend>

    <label
      v-for="num in range"
      :key="num"
      class="number flex items-center justify-center caption-bold bg-primary-primary rounded-s"
      :class="{
        'number-restricted': excludedByAddOns.includes(num),
        'number-selected': model && num <= model,
      }"
      :for="`num-${num}`"
    >
      <input
        :id="`num-${num}`"
        :checked="model === num"
        class="sr-only"
        name="high-frequency-numbers"
        type="radio"
        :value="num"
        @change="() => handleSelect(num)"
      />
      <span aria-hidden="true">{{ num }}</span>
      <span class="sr-only">
        {{
          $t('highfrequencygame.quickpickspotselector.ariaLabel', {
            count: num,
          })
        }}
      </span>
    </label>
  </fieldset>
</template>

<script lang="ts" setup>
type Props = {
  excludedByAddOns?: number[]
  max: number
  min: number
}

const { excludedByAddOns = [], max, min } = defineProps<Props>()
const model = defineModel<Maybe<number>>('modelValue', {
  default: null,
})

const range = computed(() => Array.from({ length: max - min + 1 }, (_, i) => min + i))

const handleSelect = (num: number) => {
  if (model.value === num) {
    model.value = null
  } else {
    model.value = num
  }
}
</script>

<style lang="scss" scoped>
.number {
  width: 2.5rem;
  height: 2.5rem;
  border: token('size/border/100') solid token('color/border/secondary/secondary/bold');
  cursor: pointer;

  &-restricted {
    border: 0.088rem dashed token('color/border/secondary/secondary/bold');
  }

  &-selected {
    border: token('size/border/200') solid var(--number-color-border, #1952e0);
    background: var(--number-color-background, #0f133e);
    color: var(--number-color-text, token('color/text/primary/primary-inverted'));
  }
}

.sr-only {
  position: absolute;
  overflow: hidden;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border-width: 0;
  white-space: nowrap;
}

.number {
  position: relative;

  &:has(input:focus-visible) {
    &::before {
      content: '';
      position: absolute;
      inset: -1 * token('size/space/gap/050');
      border-radius: token('size/radius/m');
      box-shadow: 0 0 0 0.1875rem token('color/border/accent/focused/focused');
    }
  }
}
</style>
